<script>
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import MessageApi from 'dashboard/api/inbox/message';
import ContactDropdownItem from 'dashboard/modules/contact/components/ContactDropdownItem.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    ContactDropdownItem,
    NextButton,
    Avatar,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      selectedContacts: [],
      searchResults: [],
      recentContacts: [],
      selectedAttachmentIds: [],
      isSearching: false,
      isForwarding: false,
      editableContent: '',
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
    }),
    currentInboxId() {
      return this.currentChat.inbox_id;
    },
    attachments() {
      return this.message.attachments || [];
    },
    isSubmitDisabled() {
      if (this.selectedContacts.length === 0) return true;
      return (
        !this.editableContent.trim() && this.selectedAttachmentIds.length === 0
      );
    },
  },
  mounted() {
    this.editableContent = this.message.content || '';
    this.selectedAttachmentIds = this.attachments.map(a => a.id);
    this.fetchRecentContacts();
  },
  methods: {
    removeContact(contact) {
      this.selectedContacts = this.selectedContacts.filter(
        c => c.id !== contact.id
      );
    },
    async fetchRecentContacts() {
      try {
        const [page1, page2] = await Promise.all([
          ContactAPI.get(1, 'last_activity_at'),
          ContactAPI.get(2, 'last_activity_at'),
        ]);
        const contacts = [...page1.data.payload, ...page2.data.payload];
        this.recentContacts = contacts;
        this.searchResults = contacts;
      } catch {
        this.recentContacts = [];
      }
    },
    async onSearchChange(query) {
      if (!query) {
        this.searchResults = this.recentContacts;
        return;
      }
      this.isSearching = true;
      try {
        const {
          data: { payload },
        } = await ContactAPI.search(query);
        this.searchResults = payload;
      } catch (error) {
        this.searchResults = [];
      } finally {
        this.isSearching = false;
      }
    },
    attachmentIcon(fileType) {
      const iconMap = {
        image: 'image',
        video: 'video',
        audio: 'speaker-1',
        file: 'document',
      };
      return iconMap[fileType] || 'document';
    },
    attachmentName(attachment) {
      if (attachment.data_url) {
        const url = attachment.data_url.split('?')[0];
        return url.split('/').pop() || 'attachment';
      }
      return 'attachment';
    },
    async downloadAttachmentAsFile(attachment) {
      const response = await fetch(attachment.data_url);
      const blob = await response.blob();
      const fileName = this.attachmentName(attachment);
      return new File([blob], fileName, { type: blob.type });
    },
    async getSelectedFiles() {
      const selected = this.attachments.filter(a =>
        this.selectedAttachmentIds.includes(a.id)
      );
      if (!selected.length) return [];
      return Promise.all(selected.map(a => this.downloadAttachmentAsFile(a)));
    },
    async onForward() {
      this.isForwarding = true;
      try {
        const files = await this.getSelectedFiles();
        await this.selectedContacts.reduce(
          (promise, contact) =>
            promise.then(() => this.forwardToContact(contact, files)),
          Promise.resolve()
        );
        useAlert(this.$t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.SUCCESS'));
        this.onClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.ERROR'));
      } finally {
        this.isForwarding = false;
      }
    },
    async getContactSourceId(contactId) {
      try {
        const {
          data: { payload },
        } = await ContactAPI.getContactableInboxes(contactId);
        const entry = payload.find(
          item => item.inbox?.id === this.currentInboxId
        );
        return entry?.source_id || '';
      } catch {
        return '';
      }
    },
    async forwardToContact(contact, files) {
      const existingConversation = await this.findOpenConversation(contact.id);
      if (existingConversation) {
        await MessageApi.create({
          conversationId: existingConversation.id,
          message: this.editableContent,
          private: false,
          files,
        });
      } else {
        const sourceId = await this.getContactSourceId(contact.id);
        await this.$store.dispatch('contactConversations/create', {
          params: {
            inboxId: this.currentInboxId,
            contactId: contact.id,
            sourceId,
            message: { content: this.editableContent },
            files,
            status: 'open',
          },
          isFromWhatsApp: false,
        });
      }
    },
    async findOpenConversation(contactId) {
      try {
        const {
          data: { payload },
        } = await ContactAPI.getConversations(contactId);
        return payload.find(
          conversation =>
            conversation.inbox_id === this.currentInboxId &&
            conversation.status === 'open'
        );
      } catch {
        return null;
      }
    },
  },
};
</script>

<template>
  <div class="forward-modal flex flex-col p-8 gap-4">
    <woot-modal-header
      :header-title="$t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.TITLE')"
      :header-content="
        $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.DESCRIPTION')
      "
    />

    <!-- Editable Message -->
    <div class="message-preview">
      <label class="text-sm font-medium text-n-slate-12">
        {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.MESSAGE_PREVIEW') }}
      </label>
      <textarea
        v-model="editableContent"
        class="mt-1 w-full p-3 rounded-md bg-n-alpha-2 text-sm text-n-slate-12 max-h-48 min-h-[6rem] resize-y border border-n-weak focus:border-n-brand focus:outline-none"
        rows="5"
      />
    </div>

    <!-- Attachments -->
    <div v-if="attachments.length" class="attachments-section">
      <label class="text-sm font-medium text-n-slate-12">
        {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.ATTACHMENTS') }}
      </label>
      <div class="mt-1 flex flex-col gap-1">
        <label
          v-for="attachment in attachments"
          :key="attachment.id"
          class="flex items-center gap-2 p-2 rounded-md bg-n-alpha-2 cursor-pointer hover:bg-n-alpha-3"
        >
          <input
            v-model="selectedAttachmentIds"
            type="checkbox"
            :value="attachment.id"
            class="form-checkbox"
          />
          <fluent-icon
            :icon="attachmentIcon(attachment.file_type)"
            size="14"
            class="text-n-slate-11"
          />
          <span class="text-sm text-n-slate-12 truncate">
            {{ attachmentName(attachment) }}
          </span>
          <span class="text-xs text-n-slate-11 ml-auto whitespace-nowrap">
            {{ attachment.file_type }}
          </span>
        </label>
      </div>
    </div>

    <!-- Contact Selector -->
    <div class="multiselect-wrap--medium">
      <label class="text-sm font-medium text-n-slate-12">
        {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.SELECT_CONTACTS') }}
      </label>
      <multiselect
        v-model="selectedContacts"
        :options="searchResults"
        multiple
        label="name"
        track-by="id"
        :internal-search="false"
        clear-on-select
        :show-labels="false"
        :placeholder="
          $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.SEARCH_PLACEHOLDER')
        "
        allow-empty
        :loading="isSearching"
        :max-height="240"
        class="mt-1 hide-tags"
        @search-change="onSearchChange"
      >
        <template #tag><span /></template>
        <template #option="props">
          <ContactDropdownItem
            :thumbnail="props.option.thumbnail"
            :identifier="props.option.id"
            :name="props.option.name"
            :email="props.option.email"
            :phone-number="props.option.phone_number"
          />
        </template>
        <template #noResult>
          <span>
            {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.NO_RESULTS') }}
          </span>
        </template>
      </multiselect>
      <!-- Selected contacts chips -->
      <div v-if="selectedContacts.length" class="mt-2 flex flex-wrap gap-2">
        <span
          v-for="contact in selectedContacts"
          :key="contact.id"
          class="selected-contact-chip"
        >
          <Avatar :src="contact.thumbnail" :name="contact.name" :size="16" />
          <span class="text-sm truncate max-w-[10rem]">
            {{ contact.name }}
          </span>
          <button class="remove-btn" @click="removeContact(contact)">
            <fluent-icon icon="dismiss" size="12" />
          </button>
        </span>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex justify-end gap-2 mt-2">
      <NextButton
        faded
        slate
        :label="$t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.CANCEL')"
        @click.prevent="onClose"
      />
      <NextButton
        :is-loading="isForwarding"
        :disabled="isSubmitDisabled"
        :label="$t('CONVERSATION.CONTEXT_MENU.FORWARD_MODAL.SUBMIT')"
        @click.prevent="onForward"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.forward-modal {
  @apply min-h-[38rem];
}

.message-preview {
  @apply w-full;
}

.hide-tags ::v-deep(.multiselect__tags-wrap) {
  @apply hidden;
}

.selected-contact-chip {
  @apply inline-flex items-center gap-1.5 px-2 py-1 rounded-full
    bg-n-alpha-2 text-n-slate-12 border border-n-weak;
}

.remove-btn {
  @apply flex items-center justify-center w-4 h-4 rounded-full
    text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-3 cursor-pointer;
}
</style>
